import dayjs from 'dayjs';

import { PAYMENT_STATES } from 'utilities/constants';
import { formatDollarsFromCents } from 'utilities/helpers';

// A plan's transactions include payments that already ran; only the remaining
// scheduled ones can be edited or re-authorized.
export const getScheduledTransactions = (paymentPlan) =>
  (paymentPlan?.transactions ?? []).filter(
    ({ status }) => status === PAYMENT_STATES.SCHEDULED,
  );

export const togglePrecoPaymentDeleted = (schedule, date) =>
  schedule.map((payment) =>
    dayjs(payment.date).isSame(dayjs(date))
      ? {
          ...payment,
          changeType: payment.changeType === 'delete' ? undefined : 'delete',
        }
      : payment,
  );

export const undoPrecoPaymentEdit = (schedule, date) =>
  schedule.map((payment) => {
    if (!dayjs(payment.date).isSame(dayjs(date)) || !payment.original) {
      return payment;
    }
    const { original } = payment;
    return {
      ...payment,
      ...original,
      changeType: undefined,
      original: undefined,
    };
  });

// Past payments carry a state; only ones that went through reduce the balance. A returned
// payment's scheduled row stays in the table, so counting it here would double the amount.
const PAID_PAYMENT_STATES = [
  PAYMENT_STATES.POSTED,
  PAYMENT_STATES.PENDING,
  PAYMENT_STATES.EXECUTED,
];

export const getPrecoScheduleTotal = (schedule) =>
  schedule
    ?.filter(
      (payment) =>
        payment.changeType !== 'delete' &&
        (!payment.type || PAID_PAYMENT_STATES.includes(payment.type)),
    )
    .reduce((total, payment) => total + payment.amount, 0) ?? 0;

export const getPrecoBalanceAlert = ({
  totalPayments,
  balance,
  outstandingBalance,
}) => {
  if (totalPayments < balance) {
    const diff = balance - totalPayments;
    return {
      type: 'error',
      message: (
        <>
          Oh no! The total is lower than the remaining balance by
          <strong> {formatDollarsFromCents(diff)}</strong>. Please increase any
          of the remaining payments to meet the remaining balance.
        </>
      ),
    };
  }

  if (totalPayments > outstandingBalance) {
    const diff = totalPayments - outstandingBalance;
    return {
      type: 'error',
      message: (
        <>
          Oops! The total is higher than the entire account balance by
          <strong> {formatDollarsFromCents(diff)}</strong>. Please lower any of
          the other scheduled payments.
        </>
      ),
    };
  }

  if (totalPayments > balance) {
    const diff = formatDollarsFromCents(totalPayments - balance);
    return {
      type: 'warning',
      message: (
        <>
          Please notify customer: You’ll be overpaying by{' '}
          <strong>{diff}</strong>. You can adjust any payment, or the extra will
          be applied to the total outstanding balance of{' '}
          {formatDollarsFromCents(outstandingBalance)}.
        </>
      ),
    };
  }

  return null;
};
